import { saleCloseParts } from "@/domain/activity/rules";
import type { SaleClose } from "@/domain/activity/types";

const saleClosePhrase = {
  before: { start: "před začátkem", end: "před koncem" },
  after: { start: "po začátku", end: "po konci" },
} as const;

function templateSaleClose(close: SaleClose): string {
  const { amount, unit, direction, anchor } = saleCloseParts(close);
  if (amount === 0) return anchor === "start" ? "při začátku" : "při konci";
  return `${amount} ${unit === "hours" ? "h" : "min"} ${saleClosePhrase[direction][anchor]}`;
}

/** The 72 hours the API opens selling and reaches ahead with
 *  (`lastgo-api/src/sale-window.ts`, model A10). Stated here in words only —
 *  the portal never computes a default bound the API owns. */
const saleHorizonHours = 72;

export const offerAuthoringCopy = {
  templateLabel: "Šablona slev",
  templatePlaceholder: "Vyberte šablonu slev",
  priceLabel: "Zlevněná cena (Kč)",
  priceOriginal: (priceOriginal: number) => `Původní: ${priceOriginal} Kč`,
  capacityLabel: "Počet míst",
  slotDateLabel: "Datum",
  slotStartLabel: "Začátek termínu",
  slotEndLabel: "Konec termínu",
  slotEndsNextDay: (day: string) => `končí následující den (${day})`,
  slotStartedAlready: "Termín už začal. Zvolte pozdější začátek.",
  slotSection: "Kdy termín probíhá",
  priceSection: "Cena a kapacita",
  customFieldsSection: "Doplňující údaje",
  saleSection: "Kdy se sleva prodává",
  saleStartLabel: "Začátek prodeje",
  saleEndLabel: "Konec prodeje",
  saleStartBlank: `Necháte-li prázdné, otevřeme prodej ${saleHorizonHours} hodin před začátkem termínu.`,
  saleEndBlank:
    "Necháte-li prázdné, ukončíme prodej za vás — nejpozději koncem termínu. Prodej po začátku termínu zadejte sami.",
  inherited: "Převzato ze šablony slev",
  overridden: (fromTemplate: string) => `Přepsáno · ze šablony slev: ${fromTemplate}`,
  restore: "Vrátit",
  restoreAria: (fromTemplate: string) => `Vrátit ${fromTemplate} ze šablony slev`,
  templateSaleLead: (hours: number) => `${hours} h před začátkem`,
  templateSaleClose,
} as const;

export const offerSeriesCopy = {
  title: "Vypsat více termínů",
  templateLabel: offerAuthoringCopy.templateLabel,
  templatePlaceholder: offerAuthoringCopy.templatePlaceholder,
  weekdaysLabel: "Dny v týdnu",
  slotStartLabel: offerAuthoringCopy.slotStartLabel,
  slotEndLabel: offerAuthoringCopy.slotEndLabel,
  rangeFromLabel: "Od data",
  rangeToLabel: "Do data",
  buildPreview: "Zobrazit přehled termínů",
  rebuildPreview: "Přepočítat přehled",
  description: "Popište, kdy se termíny opakují.",
  previewSection: "Přehled termínů",
  inheritedHeading: offerAuthoringCopy.inherited,
  priceLabel: "Zlevněná cena",
  capacityLabel: offerAuthoringCopy.capacityLabel,
  saleStartLabel: offerAuthoringCopy.saleStartLabel,
  saleStartFromTemplate: (hours: number) => offerAuthoringCopy.templateSaleLead(hours),
  saleEndLabel: offerAuthoringCopy.saleEndLabel,
  saleEndFromTemplate: offerAuthoringCopy.templateSaleClose,
  saleEndDefault: "nejpozději koncem termínu (výchozí nastavení LastGO)",
  saleStartDefault: `${offerAuthoringCopy.templateSaleLead(saleHorizonHours)} (výchozí nastavení LastGO)`,
  missingPrice: "Šablona slev cenu neurčuje",
  missingCapacity: "Šablona slev počet míst neurčuje",
  rowSaleFrom: (when: string) => `Prodej od ${when}`,
  rowSaleUntil: (when: string) => `Prodej do ${when}`,
  rowSaleDefault: `Prodej otevřeme ${saleHorizonHours} h předem`,
  slotEndsNextDay: offerAuthoringCopy.slotEndsNextDay,
  patternEndsNextDay: "končí následující den",
  rowEdited: "Čas upraven",
  rowsHint:
    "Než termíny vypíšete, můžete kterýkoli řádek upravit nebo vyhodit. Jakmile změníte zadání nad přehledem, přehled zahodíme a vytvoříte ho znovu.",
  emptyPreview: "Přehled je prázdný.",
} as const;

export const offerCustomFieldsCopy = {
  overrideLead: "Tento termín se ptá na vlastní údaje místo těch ze šablony slev — nesčítají se.",
  inheritedLead: "Zákazníka se zeptáme na:",
  takeTemplateFields: "Převzít údaje ze šablony slev",
  askDifferently: "Zeptat se u tohoto termínu jinak",
  noTemplatePicked:
    "Nejdřív vyberte šablonu slev. Pak tu uvidíte, na co se zákazníka zeptáme.",
  templateFieldsUnavailable:
    "Údaje ze šablony slev tu teď nemáme. U tohoto termínu platí to, na co se ptá šablona.",
  inheritedHeading: "Převzato ze šablony slev — zde se needitují",
  inheritedNothing:
    "Šablona slev se zákazníka na nic neptá, takže se u tohoto termínu nezeptáme na nic.",
  requiredMark: "povinné",
  unreadableOwnHeading: "Vlastní údaje, které tu neumíme zobrazit",
  unreadableOwnLead:
    "Tento termín se ptá na údaje, kterým portál nerozumí, a necháváme je beze změny. Převzetím údajů ze šablony je smažete a zákazníka se zeptáme na to, na co se ptá šablona.",
  unreadableInheritedHeading: "Údaje ze šablony slev, které tu neumíme zobrazit",
  unreadableInheritedLead:
    "Šablona slev se ptá na údaje, kterým portál nerozumí. U tohoto termínu přesto platí — dokud se nezeptáte jinak.",
} as const;
